import { useCallback, useEffect, useRef, useState } from "react";

const HEALTH_INTERVAL_MS = 60_000;
// /providers 只做並行線路探測；10 秒足以涵蓋 Manager 逾時且不讓設定頁久等。
const HEALTH_TIMEOUT_MS = 10_000;

/** 背景輪詢健康 API；只在頁面可見時更新，且同時間最多保留一個請求。 */
export function useApiHealth(apiBase) {
  const requestRef = useRef(null);
  const [health, setHealth] = useState({ phase: "loading", data: null, error: "" });

  const refresh = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, HEALTH_TIMEOUT_MS);
    setHealth((current) => ({ ...current, phase: current.data ? "refreshing" : "loading", error: "" }));
    try {
      const response = await fetch(`${apiBase}/api/v1/providers?market=TW`, {
        signal: controller.signal,
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success || !body?.data) {
        throw new Error(body?.error?.userMessage || body?.error?.message || "無法讀取 API 狀態");
      }
      setHealth({ phase: "ready", data: body.data, error: "" });
    } catch (error) {
      if (controller.signal.aborted && !timedOut) return;
      setHealth((current) => ({
        ...current,
        phase: "error",
        error: timedOut ? "API 狀態檢查逾時" : error?.message || "API 狀態暫時無法讀取",
      }));
    } finally {
      window.clearTimeout(timeout);
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [apiBase]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, HEALTH_INTERVAL_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      requestRef.current?.abort();
    };
  }, [refresh]);

  return { ...health, refresh };
}
